import React, { useState, useEffect } from 'react';
import { api } from './api.js';
import styles from './Cobrar.module.css';
import { hasPermission, PERMISSIONS, getUserData } from './permissions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUser, 
  faList, 
  faMoneyBill, 
  faClipboardCheck, 
  faPrint, 
  faWhatsapp, 
  faCheckCircle, 
  faExclamationTriangle,
  faTimesCircle,
  faCreditCard,
  faChartBar
} from '@fortawesome/free-solid-svg-icons';

// Hook para detectar si es dispositivo móvil
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile;
};

const Cobrar = () => {
  const isMobile = useIsMobile();
  const [sector, setSector] = useState('');
  const [sectorName, setSectorName] = useState('');
  const [personas, setPersonas] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [selectedPersona, setSelectedPersona] = useState(null);
  const [monto, setMonto] = useState('');
  const [sectores, setSectores] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('nombre');
  const [isAdmin, setIsAdmin] = useState(false);
  const [cobrador, setCobrador] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showReceiptOptionsModal, setShowReceiptOptionsModal] = useState(false);
  const [tempPersona, setTempPersona] = useState(null);
  const [tempMonto, setTempMonto] = useState('');
  const [tempRecibo, setTempRecibo] = useState('');
  const [pagosRealizados, setPagosRealizados] = useState([]);
  const [totalRecaudado, setTotalRecaudado] = useState(0);
  const [showCajaCierre, setShowCajaCierre] = useState(false);
  const [cuadreId, setCuadreId] = useState(null);

  // Fetch sectores from API
  useEffect(() => {
    const userData = getUserData();
    setCurrentUser(userData);
    setIsAdmin(userData?.rol === 'administrador');
    
    // Si es encargado, pre-seleccionar su sector
    if (userData?.rol === 'encargado_de_sector' && userData?.sector_id) {
      setSector(userData.sector_id);
    }

    const fetchSectores = async () => {
      try {
        const data = await api.getSectors();
        setSectores(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error fetching sectores:', error);
      }
    };

    // Cargar pagos del día desde la base de datos
    const loadDailyPayments = async () => {
      try {
        const pagosDelDia = await api.getDailyPayments();
        console.log('Pagos del día obtenidos:', pagosDelDia);
        
        if (Array.isArray(pagosDelDia)) {
          // Transformar datos para mostrar en la UI
          const pagosFormateados = pagosDelDia.map(pago => {
            const monto = parseFloat(pago.monto) || 0;
            console.log('Pago procesado:', pago.afiliado_nombre, '- Monto:', monto, '(original:', pago.monto, ')');
            return {
              id: pago.id,
              nombre: `${pago.afiliado_nombre || ''} ${pago.apellido || ''}`.trim(),
              codigo: pago.codigoAfiliado || '',
              monto: monto,
              fecha: new Date(pago.fecha).toLocaleDateString('es-ES', { year: 'numeric', month: '2-digit', day: '2-digit' }),
              sector: pago.sector || '',
              nombreCobrador: pago.nombre_cobrador || 'Sin asignar',
              horaPago: pago.hora_pago || ''
            };
          });
          
          console.log('Pagos formateados:', pagosFormateados);
          setPagosRealizados(pagosFormateados);
          
          // Calcular total recaudado sumando todos los montos
          let total = 0;
          for (let i = 0; i < pagosFormateados.length; i++) {
            total += pagosFormateados[i].monto || 0;
            console.log(`Suma iteración ${i}:`, total);
          }
          console.log('Total final calculado:', total, 'tipo:', typeof total);
          setTotalRecaudado(total);
          console.log('setTotalRecaudado ejecutado con:', total);
        }
      } catch (error) {
        console.error('Error cargando pagos del día:', error);
      }
    };

    fetchSectores();
    loadDailyPayments();
  }, []);

  // Fetch personas by sector from API
  useEffect(() => {
    const fetchPersonas = async () => {
      try {
        const data = await api.getAfiliados();
        setClientes(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error al obtener los afiliados:', error);
      }
    };
  
    fetchPersonas();
  }, []);

  // Filter the affiliates by sector and search
  useEffect(() => {
    let filteredClients = clientes;

    if (!isAdmin && sectorName) {
      filteredClients = clientes.filter((cliente) => cliente.sector === sectorName);
    }

    if (searchQuery) {
      filteredClients = filteredClients.filter((cliente) => {
        const query = searchQuery.toLowerCase();
        switch (searchType) {
          case 'nombre':
            return `${cliente.nombresTitular} ${cliente.apellidosTitular}`
              .toLowerCase()
              .includes(query);
          case 'cedula':
            return cliente.cedulaTitular && 
              cliente.cedulaTitular.toString().includes(searchQuery);
          case 'codigo':
            return cliente.codigoAfiliado && 
              cliente.codigoAfiliado.toString().includes(searchQuery);
          default:
            return true;
        }
      });
    }

    setPersonas(filteredClients);
  }, [clientes, sectorName, searchQuery, searchType, isAdmin]);

  // Handle selecting a persona
  const handlePersonaSelect = (persona) => {
    console.log('Seleccionando persona:', persona.nombresTitular, persona.apellidosTitular);
    setSelectedPersona(persona);
    setMonto(persona.totalPagarMensual);
    
    // Buscar el sector del afiliado y actualizarlo en el selector
    if (persona.sector && sectores.length > 0) {
      const sectorData = sectores.find((s) => s.nombre_sector === persona.sector);
      if (sectorData) {
        const sectorId = String(sectorData.id_sector || sectorData.id);
        setSector(sectorId);
        console.log('Sector actualizado a:', sectorId, 'Nombre:', sectorData.nombre_sector);
      } else {
        console.log('Sector no encontrado:', persona.sector, 'Disponibles:', sectores.map(s => s.nombre_sector));
      }
    }
  };

  // Handle monto input change
  const handleMontoChange = (event) => {
    setMonto(event.target.value);
  };

  const handlePago = async () => {
    if (selectedPersona && monto) {
      if (!selectedPersona.codigoAfiliado) {
        alert('Error: No se ha seleccionado un afiliado válido.');
        return;
      }

      // Preparar datos de pago desde el inicio
      const afiliado_id = selectedPersona.idAfiliado || selectedPersona.id;
      const paymentData = {
        idAfiliado: afiliado_id,
        monto: parseFloat(monto),
        fecha: new Date().toISOString().split('T')[0],
        metodo_pago: 'Efectivo',
        cobrador_id: currentUser?.id,
        nombre_cobrador: currentUser ? `${currentUser.nombre} ${currentUser.apellido}` : 'Sin asignar',
        hora_pago: new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
      };

      try {
        // Asegurarse de que tenemos el nombre del cobrador
        if (!cobrador) {
          alert('Error: No se ha podido identificar al cobrador');
          return;
        }

        // Verificar si el afiliado ya tiene un pago en el mes actual
        const payments = await api.getPayments(afiliado_id);
        
        const today = new Date();
        const currentMonth = today.getMonth();
        const currentYear = today.getFullYear();
        
        // Buscar si hay un pago en el mes actual
        const hasPaymentThisMonth = payments.some((payment) => {
          // Manejar diferentes formatos de fecha
          let paymentDate;
          if (typeof payment.fecha === 'string') {
            // Si es string "YYYY-MM-DD"
            const parts = payment.fecha.split('-');
            paymentDate = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
          } else {
            paymentDate = new Date(payment.fecha);
          }
          
          return paymentDate.getMonth() === currentMonth && 
                 paymentDate.getFullYear() === currentYear;
        });
        
        if (hasPaymentThisMonth) {
          const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
          const nextMonthName = nextMonth.toLocaleString('es-ES', { month: 'long', year: 'numeric' });
          const nextMonthDay = nextMonth.toLocaleString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
          const currentMonthName = today.toLocaleString('es-ES', { month: 'long', year: 'numeric' });
          alert(`⚠️ El pago no pudo ser procesado\n\nEste afiliado ya fue cobrado en ${currentMonthName}.\n\nPodrá registrar un nuevo pago a partir del ${nextMonthDay}.`);
          return;
        }

        // Registrar el pago en la base de datos
        
        console.log('Enviando pago al servidor:', paymentData);
        const resultPago = await api.createPayment(paymentData);
        console.log('Respuesta del servidor:', resultPago);
        
        // Recargar pagos del día desde la base de datos para reflejar cambios reales
        const pagosDelDia = await api.getDailyPayments();
        console.log('Pagos del día después de crear:', pagosDelDia);
        if (Array.isArray(pagosDelDia)) {
          const pagosFormateados = pagosDelDia.map(pago => {
            const monto = parseFloat(pago.monto) || 0;
            return {
              id: pago.id,
              nombre: `${pago.afiliado_nombre || ''} ${pago.apellido || ''}`.trim(),
              codigo: pago.codigoAfiliado || '',
              monto: monto,
              fecha: new Date(pago.fecha).toLocaleDateString('es-ES', { year: 'numeric', month: '2-digit', day: '2-digit' }),
              sector: pago.sector || '',
              nombreCobrador: pago.nombre_cobrador || 'Sin asignar',
              horaPago: pago.hora_pago || ''
            };
          });
          
          setPagosRealizados(pagosFormateados);
          
          // Recalcular total recaudado sumando todos los montos
          const total = pagosFormateados.reduce((sum, pago) => sum + (pago.monto || 0), 0);
          setTotalRecaudado(total);
        }

        const reciboHTML = `
          <html>
            <head>
              <title>Recibo de Pago</title>
              <style>
                body { font-family: Arial, sans-serif; padding: 20px; }
                .container { max-width: 600px; margin: 0 auto; border: 1px solid #ccc; padding: 20px; }
                .header { text-align: center; margin-bottom: 20px; }
                .logo { width: 120px; margin: 0 auto 10px; display: block; }
                .details { margin: 20px 0; }
                .signatures { margin-top: 40px; text-align: center; }
                .signature-line { display: inline-block; margin: 0 20px; }
              </style>
            </head>
            <body>
              <div class="container">
                <div class="header">
                  <img src="https://i.postimg.cc/jqXmv6Fr/logo2.jpg" alt="Logo" class="logo"/>
                  <h2>Escuela Laboral Dr JOSE FRANCISCO PENA GOMEZ</h2>
                  <p>Incorporada Decreto 622-01 de 25/06/2001</p>
                  <p>RNC-417-00701-9</p>
                  <p>C/Nueva Generacion #2, Galvan Prov. Bahoruco, Rep Dom</p>
                  <p>Tel: 829-881-9906, 809-248-1923</p>
                  <hr/>
                  <h3>AYUDA MUTUA</h3>
                  <h4>RECIBO DE PAGO</h4>
                </div>
                <div class="details">
                  <p><strong>Nombre:</strong> ${selectedPersona.nombresTitular} ${selectedPersona.apellidosTitular}</p>
                  <p><strong>Código:</strong> ${selectedPersona.codigoAfiliado}</p>
                  <p><strong>Cédula:</strong> ${selectedPersona.cedulaTitular}</p>
                  <p><strong>Sector:</strong> ${selectedPersona.sector}</p>
                  <p><strong>Monto Pagado:</strong> $${monto}</p>
                  <p><strong>Fecha:</strong> ${new Date().toLocaleDateString()}</p>
                  <p><strong>Cobrado por:</strong> ${cobrador || 'Sistema'}</p>
                </div>
                <div class="signatures">
                  <div class="signature-line">
                    <div style="border-top: 1px solid black; width: 150px; margin-bottom: 5px;"></div>
                    <p>Firma de ${cobrador || 'Sistema'}</p>
                  </div>
                  <div class="signature-line">
                    <div style="border-top: 1px solid black; width: 150px; margin-bottom: 5px;"></div>
                    <p>Firma del Afiliado</p>
                  </div>
                </div>
              </div>
            </body>
          </html>
        `;

        // Guardar el recibo HTML en state para usarlo después
        if (!window.reciboHTML) {
          window.reciboHTML = {};
        }
        window.reciboHTML[selectedPersona.idAfiliado] = reciboHTML;

        // Guardar datos temporalmente para el modal
        setTempPersona(selectedPersona);
        setTempMonto(monto);
        setTempRecibo(reciboHTML);
        
        // Limpiar estado actual
        setMonto('');
        setSelectedPersona(null);
        
        // PRIMERO mostrar el modal de éxito
        setShowSuccessModal(true);
        
        // DESPUÉS mostrar el modal de opciones de recibo
        setTimeout(() => {
          setShowReceiptOptionsModal(true);
        }, 3100);
      } catch (error) {
        console.error('✗ ERROR COMPLETO al procesar pago:', error);
        console.error('Error message:', error.message);
        console.error('Error response:', error.response);
        console.error('Payment data que se envió:', paymentData);
        
        let mensajeError = 'Hubo un error al procesar el pago. Por favor intente nuevamente.';
        
        if (error.response?.status === 400) {
          mensajeError = error.response?.data?.message || 'Los datos ingresados no son válidos. Verifique e intente nuevamente.';
        } else if (error.response?.status === 409) {
          mensajeError = error.response?.data?.message || 'Este afiliado ya tiene un pago registrado en el mes actual.';
        } else if (error.response?.status === 500) {
          mensajeError = 'Error en el servidor. Por favor, contacte al administrador.';
        } else if (error.message === 'Network Error') {
          mensajeError = 'Error de conexión. Verifique su conexión a internet e intente nuevamente.';
        }
        
        alert(mensajeError);
      }
    } else {
      alert('Por favor, seleccione una persona y el monto a pagar');
    }
  };

  const enviarPorWhatsApp = (persona, monto, cobrador) => {
    const mensaje = `
*RECIBO DE PAGO - AYUDA MUTUA*
Nombre: ${persona.nombresTitular} ${persona.apellidosTitular}
Código: ${persona.codigoAfiliado}
Monto: $${monto}
Fecha: ${new Date().toLocaleDateString()}
Cobrador: ${cobrador}
    `.trim();

    const whatsappURL = `https://wa.me/${persona.telefonoTitular}?text=${encodeURIComponent(mensaje)}`;
    window.open(whatsappURL, '_blank');
  };

  const handleImprimir = () => {
    if (tempRecibo) {
      const printFrame = document.createElement('iframe');
      printFrame.style.display = 'none';
      document.body.appendChild(printFrame);
      
      const frameDoc = printFrame.contentWindow.document;
      frameDoc.write(tempRecibo);
      frameDoc.close();
      
      setTimeout(() => {
        printFrame.contentWindow.focus();
        printFrame.contentWindow.print();
      }, 500);
      
      // Limpiar después de 5 segundos
      setTimeout(() => {
        if (document.body.contains(printFrame)) {
          document.body.removeChild(printFrame);
        }
      }, 5000);
    }
    
    setShowReceiptOptionsModal(false);
    setShowSuccessModal(true);
    setTimeout(() => {
      setShowSuccessModal(false);
      setTempPersona(null);
      setTempMonto('');
      setTempRecibo('');
    }, 3000);
  };

  const handleWhatsApp = () => {
    enviarPorWhatsApp(tempPersona, tempMonto, cobrador || 'Sistema');
    setShowReceiptOptionsModal(false);
    setShowSuccessModal(true);
    setTimeout(() => {
      setShowSuccessModal(false);
      setTempPersona(null);
      setTempMonto('');
      setTempRecibo('');
    }, 3000);
  };

  useEffect(() => {
    if (sector) {
      const sectorData = sectores.find((s) => 
        String(s.id_sector) === String(sector) || String(s.id) === String(sector)
      );
      if (sectorData) {
        setSectorName(sectorData.nombre_sector);
      }
    }
  }, [sector, sectores]);

  // Monitorear cambios en totalRecaudado
  useEffect(() => {
    console.log('ESTADO totalRecaudado actualizado a:', totalRecaudado);
  }, [totalRecaudado]);

  // Guardar pagos en localStorage cuando cambien
  useEffect(() => {
    try {
      localStorage.setItem('pagosRealizados', JSON.stringify(pagosRealizados));
      localStorage.setItem('totalRecaudado', totalRecaudado.toString());
    } catch (error) {
      console.error('Error guardando datos en localStorage:', error);
    }
  }, [pagosRealizados, totalRecaudado]);

  // Obtener datos del usuario logueado desde localStorage después del login
  useEffect(() => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const usuario = JSON.parse(userStr);
        setIsAdmin(usuario.rol === 'administrador');
        // El nombre viene de la BD a través del login
        const nombreUsuario = usuario.nombre || usuario.email || 'Sistema';
        setCobrador(nombreUsuario);
        setCurrentUser(usuario);
        if (usuario.sector_id) {
          setSector(usuario.sector_id);
        }
      }
    } catch (error) {
      console.error('Error al obtener datos del usuario:', error);
    }
  }, []);

  return (
    <div className={styles.container} style={{ padding: isMobile ? '12px' : '20px' }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: isMobile ? 'flex-start' : 'center',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? '12px' : '0',
        marginBottom: '24px' 
      }}>
        <h2 className={styles.title} style={{ 
          fontSize: isMobile ? '18px' : '24px',
          margin: '0'
        }}>
          {sectorName 
            ? `Cobrar - ${sectorName}` 
            : 'Gestión de Cobros'}
        </h2>
        <button
          onClick={() => setShowCajaCierre(true)}
          style={{
            background: '#003366',
            color: '#ffffff',
            border: 'none',
            padding: isMobile ? '8px 12px' : '10px 20px',
            borderRadius: '6px',
            fontSize: isMobile ? '12px' : '13px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            textTransform: 'uppercase',
            letterSpacing: '0.3px',
            width: isMobile ? '100%' : 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            justifyContent: 'center'
          }}
          onMouseEnter={(e) => e.target.style.background = '#001a33'}
          onMouseLeave={(e) => e.target.style.background = '#003366'}
        >
          <FontAwesomeIcon icon={faChartBar} /> Cierre de Caja
        </button>
      </div>

      {/* Resumen de Caja Actual + Selector de Sector */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : (isAdmin ? '1fr 1fr' : '1fr'),
        gap: isMobile ? '12px' : '20px',
        marginBottom: '24px'
      }}>
        {/* Cards de Resumen - Más Compactas */}
        <div style={{
          background: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          padding: isMobile ? '12px' : '14px 16px',
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr 1fr 1fr' : '1fr 1fr 1fr',
          gap: isMobile ? '8px' : '12px'
        }}>
          <div style={{ textAlign: 'center', padding: isMobile ? '2px' : '4px' }}>
            <p style={{ fontSize: isMobile ? '10px' : '11px', color: '#666', margin: '0 0 3px 0', textTransform: 'uppercase', fontWeight: '600' }}>Pagos</p>
            <p style={{ fontSize: isMobile ? '18px' : '20px', fontWeight: '700', color: '#003366', margin: '0' }}>{pagosRealizados.length}</p>
          </div>
          <div style={{ textAlign: 'center', padding: isMobile ? '2px' : '4px' }}>
            <p style={{ fontSize: isMobile ? '10px' : '11px', color: '#666', margin: '0 0 3px 0', textTransform: 'uppercase', fontWeight: '600' }}>Recaudado</p>
            <p style={{ fontSize: isMobile ? '16px' : '20px', fontWeight: '700', color: '#28a745', margin: '0' }}>RD$ {totalRecaudado.toLocaleString('es-ES', { minimumFractionDigits: 2 })}</p>
          </div>
          <div style={{ textAlign: 'center', padding: isMobile ? '2px' : '4px' }}>
            <p style={{ fontSize: isMobile ? '10px' : '11px', color: '#666', margin: '0 0 3px 0', textTransform: 'uppercase', fontWeight: '600' }}>Cobrador</p>
            <p style={{ fontSize: isMobile ? '11px' : '13px', fontWeight: '600', color: '#1e293b', margin: '0' }}>{cobrador || 'Sistema'}</p>
          </div>
        </div>

        {/* Selector de Sectores - Solo para Admin */}
        {isAdmin && (
          <div style={{
            background: '#ffffff',
            border: '1.5px solid #cbd5e1',
            borderRadius: '8px',
            padding: isMobile ? '12px' : '14px 16px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <label style={{
              fontSize: isMobile ? '10px' : '11px',
              fontWeight: '600',
              color: '#1e293b',
              margin: '0 0 6px 0',
              textTransform: 'uppercase'
            }}>
              Filtrar por Sector:
            </label>
            <select
              className={styles.selectInput}
              value={String(sector)}
              onChange={(e) => setSector(e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                background: '#f8fafc',
                border: '1.5px solid #cbd5e1',
                borderRadius: '6px',
                fontSize: isMobile ? '12px' : '13px',
                boxSizing: 'border-box'
              }}
            >
              <option value="">-- Todos los sectores --</option>
              {sectores.map((sectorItem) => (
                <option
                  key={sectorItem.id || sectorItem.id_sector}
                  value={String(sectorItem.id_sector || sectorItem.id)}
                >
                  {sectorItem.nombre_sector}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', 
        gap: isMobile ? '16px' : '24px' 
      }}>
        {/* Área izquierda - Selección de afiliados */}
        <div>
          <h3 style={{
            fontSize: isMobile ? '13px' : '14px',
            fontWeight: '700',
            color: '#003366',
            margin: '0 0 12px 0',
            textTransform: 'uppercase',
            letterSpacing: '0.3px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <FontAwesomeIcon icon={faUser} /> Selecciona Afiliado
          </h3>
          
          {/* Filtros Compactos */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: '8px',
            marginBottom: '12px'
          }}>
            <select 
              className={styles.filterSelect}
              onChange={(e) => setSearchType(e.target.value)}
              value={searchType}
              style={{
                padding: '8px',
                fontSize: isMobile ? '11px' : '12px',
                background: '#f8fafc',
                border: '1.5px solid #cbd5e1',
                borderRadius: '6px'
              }}
            >
              <option value="nombre">Por Nombre</option>
              <option value="cedula">Por Cédula</option>
              <option value="codigo">Por Código</option>
            </select>
            <input
              type="text"
              className={styles.filterInput}
              placeholder="Filtrar..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                padding: '8px',
                fontSize: isMobile ? '11px' : '12px',
                background: '#f8fafc',
                border: '1.5px solid #cbd5e1',
                borderRadius: '6px'
              }}
            />
          </div>

          {/* Lista de Afiliados Compacta */}
          {personas.length > 0 ? (
            <ul style={{
              listStyle: 'none',
              padding: '0',
              margin: '0 0 12px 0',
              maxHeight: isMobile ? '200px' : '280px',
              overflowY: 'auto',
              background: '#ffffff',
              border: '1px solid #e2e8f0',
              borderRadius: '8px'
            }}>
              {personas.map((persona) => (
                <li key={persona.idAfiliado} style={{
                  borderBottom: '1px solid #e2e8f0'
                }}>
                  <button
                    style={{
                      width: '100%',
                      padding: isMobile ? '8px 10px' : '10px 12px',
                      background: selectedPersona?.idAfiliado === persona.idAfiliado ? '#003366' : '#ffffff',
                      color: selectedPersona?.idAfiliado === persona.idAfiliado ? '#ffffff' : '#1e293b',
                      border: 'none',
                      fontSize: isMobile ? '12px' : '13px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'all 0.2s ease'
                    }}
                    onClick={() => handlePersonaSelect(persona)}
                    onMouseEnter={(e) => {
                      if (selectedPersona?.idAfiliado !== persona.idAfiliado) {
                        e.target.style.background = '#f0f4f8';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedPersona?.idAfiliado !== persona.idAfiliado) {
                        e.target.style.background = '#ffffff';
                      }
                    }}
                  >
                    {persona.nombresTitular} {persona.apellidosTitular}
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <div style={{
              padding: isMobile ? '15px' : '20px',
              textAlign: 'center',
              backgroundColor: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: '6px',
              margin: '0 0 12px 0',
              fontSize: isMobile ? '11px' : '12px',
              color: '#856404',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}>
              <FontAwesomeIcon icon={faExclamationTriangle} /> No hay afiliados con esos datos
            </div>
          )}

          {/* Cuadro de Pago Compacto */}
          {selectedPersona && (
            <div key={selectedPersona.idAfiliado} style={{
              background: '#ffffff',
              border: '2px solid #003366',
              borderRadius: '8px',
              padding: isMobile ? '10px' : '12px'
            }}>
              <h4 style={{
                fontSize: isMobile ? '12px' : '13px',
                fontWeight: '700',
                color: '#003366',
                margin: '0 0 10px 0',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <FontAwesomeIcon icon={faMoneyBill} /> {selectedPersona.nombresTitular} {selectedPersona.apellidosTitular}
              </h4>
              <div style={{ fontSize: isMobile ? '11px' : '12px', color: '#666', marginBottom: '10px', lineHeight: '1.4' }}>
                <div>Código: <strong>{selectedPersona.codigoAfiliado}</strong></div>
                <div>Sector: <strong>{selectedPersona.sector}</strong></div>
              </div>
              <input
                type="number"
                style={{
                  width: '100%',
                  padding: '8px',
                  background: '#f8fafc',
                  border: '1.5px solid #cbd5e1',
                  borderRadius: '6px',
                  fontSize: isMobile ? '12px' : '13px',
                  marginBottom: '10px',
                  boxSizing: 'border-box'
                }}
                value={monto}
                onChange={handleMontoChange}
                min="0"
                placeholder="Monto RD$"
              />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                <button
                  onClick={handlePago}
                  style={{
                    padding: isMobile ? '10px' : '8px',
                    background: '#28a745',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: isMobile ? '12px' : '12px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px'
                  }}
                  onMouseEnter={(e) => e.target.style.background = '#218838'}
                  onMouseLeave={(e) => e.target.style.background = '#28a745'}
                >
                  <FontAwesomeIcon icon={faCheckCircle} /> Pagar
                </button>
                <button
                  onClick={() => {
                    setSelectedPersona(null);
                    setMonto('');
                  }}
                  style={{
                    padding: isMobile ? '10px' : '8px',
                    background: '#cbd5e1',
                    color: '#334155',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: isMobile ? '12px' : '12px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px'
                  }}
                  onMouseEnter={(e) => e.target.style.background = '#94a3b8'}
                  onMouseLeave={(e) => e.target.style.background = '#cbd5e1'}
                >
                  <FontAwesomeIcon icon={faTimesCircle} /> Cancelar
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Área derecha - Resumen de pagos del día */}
        <div>
          <div style={{
            background: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            padding: isMobile ? '10px' : '12px',
            height: isMobile ? 'auto' : '100%',
            minHeight: isMobile ? '200px' : 'auto',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <h3 style={{
              fontSize: isMobile ? '13px' : '14px',
              fontWeight: '700',
              color: '#003366',
              margin: '0 0 12px 0',
              textTransform: 'uppercase',
              letterSpacing: '0.3px',
              borderBottom: '2px solid #003366',
              paddingBottom: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <FontAwesomeIcon icon={faList} /> Pagos del Día
            </h3>
            
            {pagosRealizados.length > 0 ? (
              <div style={{ 
                flex: 1,
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                {pagosRealizados.map((pago) => (
                  <div key={pago.id} style={{
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    padding: isMobile ? '6px 8px' : '8px 10px',
                    fontSize: isMobile ? '11px' : '12px'
                  }}>
                    <div style={{ fontWeight: '600', color: '#1e293b', marginBottom: '2px', fontSize: isMobile ? '11px' : '12px' }}>
                      {pago.nombre}
                    </div>
                    {(pago.codigo || pago.sector) && (
                      <div style={{ color: '#666', fontSize: isMobile ? '10px' : '11px', margin: '2px 0' }}>
                        {pago.codigo && `${pago.codigo}`}
                        {pago.codigo && pago.sector && ' • '}
                        {pago.sector && `${pago.sector}`}
                      </div>
                    )}
                    {pago.nombreCobrador && (
                      <div style={{ color: '#0077dd', fontSize: isMobile ? '10px' : '11px', margin: '2px 0', fontWeight: '500' }}>
                        👤 {pago.nombreCobrador} {pago.horaPago && `• ${pago.horaPago}`}
                      </div>
                    )}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      paddingTop: '4px',
                      borderTop: '1px solid #cbd5e1'
                    }}>
                      <span style={{ color: '#28a745', fontWeight: '700', fontSize: isMobile ? '11px' : '12px' }}>
                        RD$ {pago.monto.toLocaleString('es-ES', { minimumFractionDigits: 2 })}
                      </span>
                      <span style={{ color: '#999', fontSize: isMobile ? '10px' : '11px' }}>
                        {pago.fecha}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#999',
                fontSize: isMobile ? '11px' : '12px',
                textAlign: 'center'
              }}>
                <p style={{ margin: 0 }}>Sin pagos hoy</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal de Cierre de Caja */}
      {showCajaCierre && (
        <div style={{
          position: 'fixed',
          top: '0',
          left: '0',
          right: '0',
          bottom: '0',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: isMobile ? 'flex-end' : 'center',
          zIndex: '9999',
          padding: isMobile ? '0' : '20px'
        }}>
          <div style={{
            backgroundColor: '#ffffff',
            borderRadius: isMobile ? '12px 12px 0 0' : '12px',
            padding: isMobile ? '20px' : '30px',
            maxWidth: isMobile ? '100%' : '500px',
            width: isMobile ? '100%' : '100%',
            maxHeight: isMobile ? '90vh' : '80vh',
            overflowY: 'auto',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)'
          }}>
            <h2 style={{
              fontSize: isMobile ? '18px' : '22px',
              fontWeight: '700',
              color: '#003366',
              margin: '0 0 8px 0',
              textTransform: 'uppercase',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <FontAwesomeIcon icon={faChartBar} /> Cierre de Caja
            </h2>
            <p style={{
              fontSize: isMobile ? '12px' : '13px',
              color: '#666',
              margin: '0 0 20px 0'
            }}>
              {new Date().toLocaleDateString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </p>

            <div style={{
              background: '#f8fafc',
              border: '2px solid #003366',
              borderRadius: '8px',
              padding: isMobile ? '12px' : '16px',
              marginBottom: '20px'
            }}>
              <div style={{ marginBottom: '12px' }}>
                <p style={{ fontSize: isMobile ? '11px' : '12px', color: '#666', margin: '0 0 4px 0', textTransform: 'uppercase', fontWeight: '600' }}>Total de Pagos</p>
                <p style={{ fontSize: isMobile ? '24px' : '32px', fontWeight: '700', color: '#003366', margin: '0' }}>{pagosRealizados.length}</p>
              </div>
              <div>
                <p style={{ fontSize: isMobile ? '11px' : '12px', color: '#666', margin: '0 0 4px 0', textTransform: 'uppercase', fontWeight: '600' }}>Total Recaudado</p>
                <p style={{ fontSize: isMobile ? '22px' : '32px', fontWeight: '700', color: '#28a745', margin: '0' }}>RD$ {totalRecaudado.toLocaleString('es-ES', { minimumFractionDigits: 2 })}</p>
              </div>
            </div>

            <div style={{
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              padding: isMobile ? '10px' : '12px',
              marginBottom: '20px',
              maxHeight: isMobile ? '200px' : '300px',
              overflowY: 'auto'
            }}>
              <h4 style={{
                fontSize: isMobile ? '12px' : '13px',
                fontWeight: '700',
                color: '#1e293b',
                margin: '0 0 12px 0',
                textTransform: 'uppercase'
              }}>Detalle de Pagos:</h4>
              {pagosRealizados.map((pago) => (
                <div key={pago.id} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: isMobile ? '6px 0' : '8px 0',
                  borderBottom: '1px solid #e2e8f0',
                  fontSize: isMobile ? '11px' : '12px'
                }}>
                  <div>
                    <div style={{ color: '#1e293b', fontWeight: '500' }}>{pago.nombre}</div>
                    {pago.nombreCobrador && (
                      <div style={{ color: '#0077dd', fontSize: isMobile ? '9px' : '10px', marginTop: '2px' }}>
                        👤 {pago.nombreCobrador} {pago.horaPago && `• ${pago.horaPago}`}
                      </div>
                    )}
                  </div>
                  <span style={{ color: '#28a745', fontWeight: '600' }}>RD$ {pago.monto.toLocaleString('es-ES', { minimumFractionDigits: 2 })}</span>
                </div>
              ))}
            </div>

            <div style={{
              display: 'flex',
              gap: isMobile ? '8px' : '12px',
              justifyContent: isMobile ? 'stretch' : 'flex-end',
              flexDirection: isMobile ? 'column' : 'row'
            }}>
              <button
                onClick={() => setShowCajaCierre(false)}
                style={{
                  padding: isMobile ? '10px' : '10px 20px',
                  background: '#cbd5e1',
                  color: '#334155',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: isMobile ? '12px' : '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => e.target.style.background = '#94a3b8'}
                onMouseLeave={(e) => e.target.style.background = '#cbd5e1'}
              >
                Cerrar
              </button>
              <button
                onClick={() => {
                  const ventana = window.open('', '', 'height=400,width=600');
                  ventana.document.write(`
                    <html><head><title>Cierre de Caja</title></head><body style="font-family: Arial; padding: 20px;">
                    <h2>CIERRE DE CAJA</h2>
                    <p><strong>Fecha:</strong> ${new Date().toLocaleDateString('es-ES')}</p>
                    <p><strong>Cobrador:</strong> ${cobrador || 'Sistema'}</p>
                    <p><strong>Total Pagos:</strong> ${pagosRealizados.length}</p>
                    <p><strong>Total Recaudado:</strong> RD$ ${totalRecaudado.toLocaleString('es-ES', { minimumFractionDigits: 2 })}</p>
                    </body></html>
                  `);
                  ventana.print();
                }}
                style={{
                  padding: isMobile ? '10px' : '10px 20px',
                  background: '#003366',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: isMobile ? '12px' : '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px'
                }}
                onMouseEnter={(e) => e.target.style.background = '#001a33'}
                onMouseLeave={(e) => e.target.style.background = '#003366'}
              >
                <FontAwesomeIcon icon={faPrint} /> Imprimir
              </button>
              <button
                onClick={() => {
                  if (window.confirm('¿Estás seguro de que deseas cerrar la caja? Se guardarán todos los pagos y se iniciará un nuevo período.')) {
                    // Cerrar caja localmente - Los pagos ya están guardados en la BD
                    setPagosRealizados([]);
                    setTotalRecaudado(0);
                    setCuadreId(null);
                    setShowCajaCierre(false);
                    
                    // Limpiar datos de la sesión
                    localStorage.setItem('pagosRealizados', JSON.stringify([]));
                    localStorage.setItem('totalRecaudado', '0');
                    
                    alert('✓ Caja cerrada exitosamente. Los datos se han guardado en la base de datos. Se inicia un nuevo período.');
                  }
                }}
                style={{
                  padding: isMobile ? '10px' : '10px 20px',
                  background: '#dc3545',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: isMobile ? '12px' : '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px'
                }}
                onMouseEnter={(e) => e.target.style.background = '#c82333'}
                onMouseLeave={(e) => e.target.style.background = '#dc3545'}
              >
                <FontAwesomeIcon icon={faCheckCircle} /> Confirmar Cierre
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de éxito */}
      {showSuccessModal && (
        <div style={{
          position: 'fixed',
          top: '0',
          left: '0',
          right: '0',
          bottom: '0',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: '9999',
          padding: isMobile ? '20px' : '0'
        }}>
          <div style={{
            backgroundColor: '#fff',
            borderRadius: '12px',
            padding: isMobile ? '30px 20px' : '40px',
            textAlign: 'center',
            maxWidth: isMobile ? '100%' : '400px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
            animation: 'slideIn 0.3s ease-in-out'
          }}>
            <div style={{
              fontSize: isMobile ? '48px' : '60px',
              marginBottom: '20px',
              animation: 'scaleIn 0.5s ease-out',
              color: '#28a745'
            }}>
              <FontAwesomeIcon icon={faCheckCircle} />
            </div>
            <h2 style={{
              color: '#28a745',
              margin: '0 0 10px 0',
              fontSize: isMobile ? '20px' : '24px',
              fontWeight: 'bold'
            }}>
              ¡Pago Registrado!
            </h2>
            <p style={{
              color: '#666',
              margin: '10px 0 0 0',
              fontSize: isMobile ? '13px' : '14px'
            }}>
              El pago se ha procesado exitosamente
            </p>
            <style>{`
              @keyframes slideIn {
                from {
                  transform: translateY(-30px);
                  opacity: 0;
                }
                to {
                  transform: translateY(0);
                  opacity: 1;
                }
              }
              @keyframes scaleIn {
                from {
                  transform: scale(0.5);
                  opacity: 0;
                }
                to {
                  transform: scale(1);
                  opacity: 1;
                }
              }
            `}</style>
          </div>
        </div>
      )}

      {/* Modal de opciones de recibo */}
      {showReceiptOptionsModal && (
        <div style={{
          position: 'fixed',
          top: '0',
          left: '0',
          right: '0',
          bottom: '0',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: '9999',
          padding: isMobile ? '20px' : '0'
        }}>
          <div style={{
            backgroundColor: '#fff',
            borderRadius: '12px',
            padding: isMobile ? '30px 20px' : '40px',
            textAlign: 'center',
            maxWidth: isMobile ? '100%' : '450px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
            animation: 'slideIn 0.3s ease-in-out'
          }}>
            <div style={{
              fontSize: isMobile ? '40px' : '48px',
              marginBottom: '20px',
              animation: 'scaleIn 0.5s ease-out',
              color: '#003366'
            }}>
              <FontAwesomeIcon icon={faClipboardCheck} />
            </div>
            <h2 style={{
              color: '#333',
              margin: '0 0 10px 0',
              fontSize: isMobile ? '20px' : '24px',
              fontWeight: 'bold'
            }}>
              Compartir Recibo
            </h2>
            <p style={{
              color: '#666',
              margin: '10px 0 30px 0',
              fontSize: isMobile ? '13px' : '14px'
            }}>
              ¿Cómo deseas recibir el comprobante de pago?
            </p>
            
            <div style={{
              display: 'flex',
              gap: isMobile ? '10px' : '12px',
              justifyContent: 'center',
              flexWrap: 'wrap',
              flexDirection: isMobile ? 'column' : 'row'
            }}>
              <button
                onClick={handleImprimir}
                style={{
                  padding: isMobile ? '12px' : '12px 24px',
                  backgroundColor: '#007bff',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: isMobile ? '13px' : '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  flex: isMobile ? '1' : 'auto',
                  minWidth: isMobile ? 'auto' : '150px'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#0056b3'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#007bff'}
              >
                🖨️ Imprimir
              </button>
              <button
                onClick={handleWhatsApp}
                style={{
                  padding: isMobile ? '12px' : '12px 24px',
                  backgroundColor: '#25D366',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: isMobile ? '13px' : '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  flex: isMobile ? '1' : 'auto',
                  minWidth: isMobile ? 'auto' : '150px'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#1eaf50'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#25D366'}
              >
                📱 WhatsApp
              </button>
            </div>
            
            <style>{`
              @keyframes slideIn {
                from {
                  transform: translateY(-30px);
                  opacity: 0;
                }
                to {
                  transform: translateY(0);
                  opacity: 1;
                }
              }
              @keyframes scaleIn {
                from {
                  transform: scale(0.5);
                  opacity: 0;
                }
                to {
                  transform: scale(1);
                  opacity: 1;
                }
              }
            `}</style>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cobrar;